import React from "react";
import { motion } from "framer-motion";
import { Check, ChevronRight, Star, Gift } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const basicCheckoutUrl = import.meta.env.VITE_BASIC_CHECKOUT_URL || '';
const premiumCheckoutUrl = import.meta.env.VITE_PREMIUM_CHECKOUT_URL || '';
const eliteCheckoutUrl = import.meta.env.VITE_ELITE_CHECKOUT_URL || '';

export const packages = {
  free: {
    name: "Free Match",
    price: 0,
    description: "5 Free Matches.",
    features: [
      "5 job matches",
      "Up to 99% Accuracy",
      "Results within minutes",
      "View results online",
      "Receive results by email",
    ],
    id: "free",
    isPopular: false,
    isFree: true,
    checkoutUrl: null
  },
  basic: {
    name: "Basic Match",
    price: 9.99,
    description: "Kickstart Your Job Search.",
    features: [
      "10 job matches",
      "Higher Accuracy",
      "Up to 99% Accuracy",
      "Results within minutes",
      "View results online",
      "Receive results by email",
      "No recurring charges"
    ],
    id: "basic",
    isPopular: false,
    checkoutUrl: basicCheckoutUrl
  },
  premium: {
    name: "Premium Match",
    price: 19.99,
    description: "Twice the Opportunities.",
    features: [
      "20 job matches",
      "Higher Accuracy",
      "Up to 99% Accuracy",
      "Results within minutes",
      "View results online",
      "Receive results by email",
      "No recurring charges"
    ],
    id: "premium",
    isPopular: true,
    checkoutUrl: premiumCheckoutUrl
  },
  professional: {
    name: "Elite Match",
    price: 29.99,
    description: "Maximize Your Job Options.",
    features: [
      "30 job matches",
      "Higher Accuracy",
      "Up to 99% Accuracy",
      "Results within minutes",
      "View results online",
      "Receive results by email",
      "No recurring charges"
    ],
    id: "professional",
    isPopular: false,
    checkoutUrl: eliteCheckoutUrl
  }
};

export default function PricingSection({ selectedPackage, onPackageSelect }) {
  const handlePackageSelect = (packageId) => {
    onPackageSelect(packageId);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
      <div className="py-16 md:py-24">
        <div className="text-center mb-16">
          <motion.span
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="px-4 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full inline-block mb-4"
          >
            PRICING
          </motion.span>
          <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-bold text-gray-900"
          >
            Choose Your Package
          </motion.h2>
          <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              viewport={{ once: true }}
              className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto"
          >
            Select the package that best fits your career goals and budget
          </motion.p>
        </div>

        <div className="mt-10 grid gap-8 lg:grid-cols-4">
          {Object.keys(packages).map((key, index) => {
            const pkg = packages[key];
            return (
                <motion.div
                    key={pkg.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="h-full flex"
                >
                  <div className="relative rounded-xl overflow-visible w-full">
                    {(pkg.isPopular || pkg.isFree) && (
                        <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                    <span className={cn(
                        "flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium shadow-md",
                        pkg.isPopular ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white" : "",
                        pkg.isFree ? "bg-gradient-to-r from-green-400 to-green-500 text-white" : ""
                    )}>
                      {pkg.isPopular && (
                          <>
                            <Star className="w-3 h-3 fill-current" /> Most Popular
                          </>
                      )}
                      {pkg.isFree && (
                          <>
                            <Gift className="w-3 h-3 fill-current" /> For Free
                          </>
                      )}
                    </span>
                        </div>
                    )}

                    <Card
                        className={cn(
                            "h-full transition-all rounded-xl overflow-hidden border bg-white flex flex-col",
                            pkg.isPopular
                                ? "shadow-xl border-blue-200 hover:shadow-2xl hover:-translate-y-1"
                                : "hover:shadow-lg hover:-translate-y-1"
                        )}
                    >
                      {pkg.isPopular && (
                          <div className="w-full h-1 bg-gradient-to-r from-blue-500 to-indigo-500" />
                      )}
                      {pkg.isFree && (
                          <div className="w-full h-1 bg-gradient-to-r from-green-400 to-green-500" />
                      )}

                      <CardContent className={cn(
                          "p-6 flex flex-col flex-grow",
                          pkg.isPopular ? "bg-gradient-to-b from-blue-50 to-white" : pkg.isFree ? "bg-gradient-to-b from-green-50 to-white" : ""
                      )}>
                        <div className="text-center mb-auto">
                          <h3 className={cn(
                              "text-2xl font-bold",
                              pkg.isPopular ? "text-blue-600" : pkg.isFree ? "text-green-600" : "text-gray-900"
                          )}>
                            {pkg.name}
                          </h3>
                          <div className="mt-4">
                        <span className="text-4xl font-bold text-gray-900">
                          {pkg.price === 0 ? "$0" : `$${pkg.price.toFixed(2)}`}
                        </span>
                          </div>
                          <p className="mt-4 text-gray-600 min-h-[30px]">{pkg.description}</p>
                        </div>

                        <ul className="mt-8 space-y-4 flex-grow">
                          {pkg.features.map((feature, i) => (
                              <li key={i} className="flex items-center">
                                <div
                                    className={cn(
                                        "flex-shrink-0 h-5 w-5 rounded-full flex items-center justify-center",
                                        pkg.isPopular ? "bg-blue-100" : pkg.isFree ? "bg-green-100" : "bg-gray-100"
                                    )}
                                >
                                  <Check
                                      className={cn(
                                          "h-3 w-3",
                                          pkg.isPopular ? "text-blue-600" : pkg.isFree ? "text-green-600" : "text-gray-600"
                                      )}
                                  />
                                </div>
                                <span className="ml-3 text-gray-600">{feature}</span>
                              </li>
                          ))}
                        </ul>

                        <Button
                            className={cn(
                                "mt-8 w-full group-hover:scale-105 transition-transform",
                                pkg.isFree
                                    ? "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
                                    : pkg.isPopular
                                        ? "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                                        : "bg-gradient-to-r from-gray-800 to-gray-900 hover:from-gray-900 hover:to-black"
                            )}
                            onClick={() => handlePackageSelect(pkg.id)}
                        >
                      <span className="flex items-center">
                        {pkg.id === "free" ? "Try for Free" : "Select Package"}
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </span>
                        </Button>
                      </CardContent>
                    </Card>
                  </div>
                </motion.div>
            );
          })}
        </div>
      </div>
  );
}